import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DataAccessExhibitorService, DataAccessFairsFacade } from '@planone/bau365/data-access-fair-data';
import { UserService } from '@planone/bau365/data-access-user';
import { ProductGroup } from '@planone/bau365/util-fair-data';
import { EMPTY, Observable, of, withLatestFrom } from 'rxjs';

import { catchError, expand, map, reduce } from 'rxjs/operators';

import { SearchSettings } from './search-api-request.models';

import { SearchAutocompleteSuggestion, SearchDocument, SearchResultsApiResponse } from './search-api-response.models';

import { mapApiResultToFrontend } from './search-api.utils';

import { SEARCH_SERVICE_CONFIG, SearchServiceConfigInterface } from '../config/search-service.config.interface';
import { SearchFilters, SearchType } from '../model/search-filter';
import { SearchFilterOptionsEntity, SearchFilterType } from '../state/option-ids-visible/search-filter-options.models';
import {
  SearchFilterLabelsEntity,
  SearchFilterLabelsEntityType,
  SearchFilterLabelsSort,
} from '../state/option-labels/search-filter-labels.models';
import { SearchResult, SearchResultsCounts } from '../state/search-results/data-access-search.models';

const defaultSearchSettings: SearchSettings = {
  search_string: '',
  search_type: ['ALL'],
  sort: {
    by: 'RECOMMENDED',
    order: 'ASC',
  },
};

const allSearchTypeAlias = [SearchType.exhibitor, SearchType.product, SearchType.post, SearchType.event];

@Injectable()
export class DataAccessSearchService {
  searchApiURL: string | null = null;
  searchItemsPerPage = 1;

  onboardingApiURL: string | null = null;
  isBauInsights$: Observable<boolean>;

  constructor(
    @Inject(SEARCH_SERVICE_CONFIG) searchServiceConfig: SearchServiceConfigInterface,
    private httpClient: HttpClient,
    private userService: UserService,
    private exhibitorsService: DataAccessExhibitorService,
    private fairsFacade: DataAccessFairsFacade
  ) {
    this.searchApiURL = searchServiceConfig?.searchApiURL;
    this.searchItemsPerPage = searchServiceConfig?.itemsPerPage;
    this.onboardingApiURL = searchServiceConfig?.fairDataGatewayURL;
    this.isBauInsights$ = this.fairsFacade.isBauInsights$;
  }

  getSearchResults(
    filters: SearchFilters,
    fairId: string,
    searchItemsPerPage?: number
  ): Observable<{ results: SearchResult[]; counts: SearchResultsCounts; filterOptions: SearchFilterOptionsEntity[] }> {
    let size = this.searchItemsPerPage;
    searchItemsPerPage ? (size = searchItemsPerPage) : '';
    const from = filters.page ? (parseInt(<string>filters.page) - 1) * size : 0;
    const defaultSearchParameters = new HttpParams().appendAll({ from: from, size: size, with_personalization: true });

    let normalizedSearchTypes = filters.searchTypes;
    if (filters.searchTypes?.[0] === SearchType.all) {
      normalizedSearchTypes = allSearchTypeAlias;
    }

    return this.httpClient
      .post<SearchResultsApiResponse>(
        `${this.searchApiURL}/fair/${fairId}/search`,
        {
          ...defaultSearchSettings,
          search_type: normalizedSearchTypes,
          search_string: filters.searchTerm,
          string_filter: filters.stringFilter,
          date_filter: filters.dateFilter,
          sort: {
            by: filters.searchSort?.split('_')[0],
            order: filters.searchSort?.split('_')[1],
          },
        },
        {
          withCredentials: true,
          params: defaultSearchParameters,
        }
      )
      .pipe(
        withLatestFrom(this.isBauInsights$),
        map(([response, isBauInsights]) => {
          return {
            results: response.results.map((apiResult) => mapApiResultToFrontend(apiResult)),
            counts: response.counts,
            filterOptions:
              response.facets?.string_facets
                ?.filter((facet) => this.isSupportedStringFacet(facet))
                .map((facet) => this.mapApiFacetToFrontend(facet, isBauInsights)) ?? [],
          };
        })
      );
  }

  getExhibitorFilterLabel(exhibitorId: string, fairId: string): Observable<SearchFilterLabelsEntity> {
    return this.exhibitorsService.getExhibitor(fairId, exhibitorId).pipe(
      map((exhibitor) => ({
        key: 'exhibitor',
        label: $localize`:@@search-service-exhibitors-label:Aussteller`,
        type: SearchFilterLabelsEntityType.simple,
        values: {
          exhibitor: exhibitor.name,
        },
      }))
    );
  }

  getStaticFilterLabels(): Observable<SearchFilterLabelsEntity[]> {
    // @ts-ignore
    return of([
      // just like halls and cities the values are not and will not be available
      // through a dedicated fair-data-gateway endpoint. but unlike the above mentioned
      // they have no user-friendly names.
      {
        key: 'category',
        label: $localize`:@@search-service-category-label:Beitragskategorie`,
        type: SearchFilterLabelsEntityType.simple,
        values: {
          EDITORIAL: $localize`:@@search-service-category-editoral-label:Redaktioneller Inhalt`,
          PRESS_RELEASE: $localize`:@@search-service-category-press-release-label:Pressemitteilung`,
        },
      },
      // see above explanation
      {
        key: 'productAtBooth',
        label: $localize`:@@search-service-productAtBooth-label:Produkt auf der Messe`,
        type: SearchFilterLabelsEntityType.simple,
        values: {
          YES: $localize`:@@search-service-productAtBooth-true-label:Produkt auf der Messe`,
        },
      },
      {
        key: 'startup',
        label: $localize`:@@search-service-startup-label:Startups`,
        type: SearchFilterLabelsEntityType.simple,
        values: {
          YES: $localize`:@@search-service-startup-true-label:Startup`,
        },
      },
      {
        key: 'eventDuration',
        label: $localize`:@@search-service-eventDuration-label:Eventdauer`,
        type: SearchFilterLabelsEntityType.simple,
        sortOrder: SearchFilterLabelsSort.fixed,
        values: {
          'up-to-15m': $localize`:@@search-service-eventDuration-upto15m-label:bis 15 Minuten`,
          'up-to-30m': $localize`:@@search-service-eventDuration-upto30m-label:bis 30 Minuten`,
          'up-to-1h': $localize`:@@search-service-eventDuration-upto1h-label:bis 1 Stunde`,
          'more-than-1h': $localize`:@@search-service-eventDuration-morethan1hour-label:mehr als 1 Stunde`,
        },
      },
      {
        key: 'eventStartsAt',
        label: $localize`:@@search-service-eventStartTime-label:Startzeitpunkt`,
        type: SearchFilterLabelsEntityType.simple,
        values: {
          '06:00-10:00': $localize`:@@search-service-eventStartsAt-6-10-label:06.00 - 10.00 Uhr`,
          '10:00-14:00': $localize`:@@search-service-eventStartsAt-10-14-label:10.00 - 14.00 Uhr`,
          '14:00-18:00': $localize`:@@search-service-eventStartsAt-14-18-label:14.00 - 18.00 Uhr`,
          '18:00-22:00': $localize`:@@search-service-eventStartsAt-18-22-label:18.00 - 22.00 Uhr`,
        },
      },
    ]);
  }

  getTopicLabels(fairId: string): Observable<SearchFilterLabelsEntity[]> {
    return this.userService.getAvailableTopics(fairId).pipe(
      expand((response) =>
        response.cursors.next ? this.userService.getAvailableTopics(fairId, response.cursors.next) : EMPTY
      ),
      reduce(
        (overallTopics, currentResponse) => ({
          ...overallTopics,
          ...currentResponse.topics.reduce((accumulatedResponseTopics: { [key: string]: string }, topic) => {
            accumulatedResponseTopics[topic.id] = topic.label;
            return accumulatedResponseTopics;
          }, {}),
        }),
        {}
      ),
      // @ts-ignore
      map((values) => [
        {
          key: 'topic',
          label: $localize`:@@search-service-topics-label:Themen`,
          type: SearchFilterLabelsEntityType.simple,
          values,
        },
      ]),
      catchError(() => of([]))
    );
  }

  getProductGroupLabels(fairId: string): Observable<SearchFilterLabelsEntity[]> {
    // @ts-ignore
    return this.userService.getAvailableProductGroups(fairId, 4, '', 1000).pipe(
      expand((response) =>
        response.cursors.next
          ? this.userService.getAvailableProductGroups(fairId, 4, response.cursors.next, 1000)
          : EMPTY
      ),
      reduce(
        (overallProductGroups, currentResponse) =>
          [
            ...overallProductGroups,
            ...currentResponse.productGroups.map((productGroup) => ({
              key: productGroup.id,
              label: productGroup.label,
              root_path: productGroup.root_path,
              external_id: productGroup.external_id,
            })),
          ] as ProductGroup[],
        [] as ProductGroup[]
      ),
      map((values) => [
        {
          key: 'productGroup',
          label: $localize`:@@search-service-productgroups-label:Produktgruppen`,
          type: SearchFilterLabelsEntityType.nested,
          values,
        },
      ]),
      catchError(() => of([]))
    );
  }

  getProfessionLabels(fairId: string): Observable<SearchFilterLabelsEntity[]> {
    // @ts-ignore
    return this.userService.getAvailableProfessions(fairId).pipe(
      map(({ professions }) =>
        professions.map((profession) => [
          {
            key: profession.id,
            external_id: profession.external_id,
            label: profession.label,
            root_path: [],
            inaccessible: true,
          },
          ...profession.specializations.map((specialization) => ({
            key: specialization.id,
            external_id: specialization.external_id,
            label: specialization.label,
            root_path: [profession.id],
          })),
        ])
      ),
      map((professionsSpecializations) =>
        professionsSpecializations.reduce((accumulated, value) => accumulated.concat(value))
      ),
      map((values) => [
        {
          key: 'profession',
          label: $localize`:@@search-service-profession-label:Berufsgruppe`,
          type: SearchFilterLabelsEntityType.nested,
          values,
        },
      ]),
      catchError(() => of([]))
    );
  }

  getAutocompleteSuggestion(
    searchTermFragment: string,
    fairId: string
  ): Observable<{ suggestions: SearchAutocompleteSuggestion[] }> {
    return this.httpClient.post<{ suggestions: SearchAutocompleteSuggestion[] }>(
      `${this.searchApiURL}/fair/${fairId}/search/as-you-type`,
      {
        search_type: ['ALL'],
        search_string: searchTermFragment,
      }
    );
  }

  getDisplayNameAutocompleteSuggestion(
    entities: SearchType[],
    displayNameFragment: string,
    fairId: string
  ): Observable<{ suggestions: SearchAutocompleteSuggestion[] }> {
    return this.httpClient.post<{ suggestions: SearchAutocompleteSuggestion[] }>(
      `${this.searchApiURL}/fair/${fairId}/search/display-name/as-you-type`,
      {
        search_type: entities,
        search_string: displayNameFragment,
      }
    );
  }

  generateSearchDocument(fairId: string, filters: SearchFilters): Observable<SearchDocument> {
    let normalizedSearchTypes = filters.searchTypes;
    if (filters.searchTypes?.[0] === SearchType.all) {
      normalizedSearchTypes = allSearchTypeAlias;
    }
    return this.httpClient.post<SearchDocument>(`${this.searchApiURL}/fair/${fairId}/search/document`, {
      ...defaultSearchSettings,
      search_type: normalizedSearchTypes,
      search_string: filters.searchTerm ?? '',
      string_filter: filters.stringFilter ?? undefined,
      date_filter: filters.dateFilter ?? undefined,
      sort: {
        by: filters.searchSort?.split('_')[0],
        order: filters.searchSort?.split('_')[1],
      },
    });
  }

  getSearchTermSuggestions(filters: SearchFilters, fairId: string): Observable<{ suggestions: string[] }> {
    let normalizedSearchTypes = filters.searchTypes;
    if (filters.searchTypes?.[0] === SearchType.all) {
      normalizedSearchTypes = allSearchTypeAlias;
    }

    return this.httpClient.post<{ suggestions: string[] }>(`${this.searchApiURL}/fair/${fairId}/search/suggest`, {
      search_type: normalizedSearchTypes,
      search_string: filters.searchTerm,
      sort: {
        by: filters.searchSort?.split('_')[0],
        order: filters.searchSort?.split('_')[1],
      },
      string_filter: filters.stringFilter,
    });
  }

  private mapApiFacetToFrontend(
    facet: {
      name: string;
      types: { name: string; count: number; personalized?: boolean }[];
    },
    isBauInsights: boolean
  ): SearchFilterOptionsEntity {
    return {
      ...this.getSupportedFilterTypeMapping(facet.name, isBauInsights),
      key: facet.name,
      values: facet.types.map((type) => {
        return { key: type.name, count: type.count, personalized: !!type.personalized };
      }),
    };
  }

  isSupportedStringFacet(facet: { name: string }) {
    return !!this.getSupportedFilterTypeMapping(facet.name, false);
  }

  getSupportedFilterTypeMapping(
    name: string,
    isBauInsights: boolean
  ): { filterType: SearchFilterType; label: string; icon?: string } {
    const supportedFilterTypeMapping: {
      [key: string]: { filterType: SearchFilterType; label: string; icon?: string };
    } = {
      category: {
        filterType: SearchFilterType.identity,
        label: $localize`:@@data-access-search-service-filter-category:Beitragskategorie`,
      },
      eventDuration: {
        filterType: SearchFilterType.identity,
        label: $localize`:@@data-access-search-service-filter-eventDuration:Eventdauer`,
      },
      eventDate: {
        filterType: SearchFilterType.simple,
        label: $localize`:@data-access-search-service-filter-eventDate:Eventdatum`,
      },
      eventLanguage: {
        filterType: SearchFilterType.simple,
        label: $localize`:@@data-access-search-service-filter-eventLanguage:Eventsprache`,
      },
      eventLocation: {
        filterType: SearchFilterType.simple,
        label: $localize`:@@data-access-search-service-filter-eventLocation:Veranstaltungsort`,
      },
      eventStartsAt: {
        filterType: SearchFilterType.identity,
        label: $localize`:@@data-access-search-service-filter-eventStartTime:Startzeitpunkt`,
      },
      eventType: {
        filterType: SearchFilterType.simple,
        label: $localize`:@@data-access-search-service-filter-eventType:Eventkategorie`,
      },
      exhibitionHall: {
        filterType: SearchFilterType.simple,
        label: $localize`:@@data-access-search-service-filter-exhibitionHall:Messehalle`,
      },
      productAtBooth: {
        filterType: SearchFilterType.identity,
        label: $localize`:@@data-access-search-service-filter-productAtBooth:Produkt auf der Messe`,
      },
      productGroup: {
        filterType: SearchFilterType.nested,
        label: $localize`:@@data-access-search-service-filter-productGroup:Produktgruppe`,
      },
      productCountryAvailability: {
        filterType: SearchFilterType.simple,
        label: $localize`:@@data-access-search-service-filter-productCountryAvailability:Länderverfügbarkeit`,
      },
      profession: {
        filterType: SearchFilterType.identity,
        label: $localize`:@@data-access-search-service-filter-profession:Berufsgruppe`,
      },
      startup: {
        filterType: SearchFilterType.identity,
        label: $localize`:@@data-access-search-service-filter-startup:Startup`,
      },
      topic: {
        filterType: SearchFilterType.identity,
        label: $localize`:@@data-access-search-service-filter-topic:Thema`,
      },
      country: {
        filterType: SearchFilterType.simple,
        label: isBauInsights
          ? $localize`:@@data-access-search-service-filter-countryCompany:Land des Unternehmens`
          : $localize`:@@data-access-search-service-filter-countryExhibitor:Land des Ausstellers`,
      },
    };
    return supportedFilterTypeMapping[name];
  }
}
